import * as turf from "@turf/turf";
import type { LatLng } from "./types";

// ─── Haversine ────────────────────────────────────────────────────────────────

export function haversine(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const slat = Math.sin(dLat / 2);
  const slng = Math.sin(dLng / 2);
  return R * 2 * Math.atan2(
    Math.sqrt(slat * slat + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * slng * slng),
    Math.sqrt(1 - slat * slat - slng * slng),
  );
}

/**
 * Return the subset of the polyline whose cumulative travel time from start
 * falls between minS and maxS seconds.
 */
export function routeSegment(
  geometry: LatLng[],
  totalDurationS: number,
  minS: number,
  maxS: number,
): LatLng[] {
  if (geometry.length < 2) return [];

  const totalDistM = geometry.slice(1).reduce((s, b, i) => s + haversine(geometry[i], b), 0);
  const msPerMetre = totalDurationS / totalDistM;
  const result: LatLng[] = [];
  let cumDistM = 0;
  let started = false;

  for (let i = 1; i < geometry.length; i++) {
    const a = geometry[i - 1];
    const b = geometry[i];
    const segLen    = haversine(a, b);
    const segDurS   = segLen * msPerMetre;
    const segStartS = cumDistM * msPerMetre;
    const segEndS   = segStartS + segDurS;

    if (segEndS >= minS && segStartS <= maxS) {
      const t1 = Math.max(0, (minS - segStartS) / segDurS);
      const t2 = Math.min(1, (maxS - segStartS) / segDurS);
      if (!started) {
        result.push({ lat: a.lat + t1 * (b.lat - a.lat), lng: a.lng + t1 * (b.lng - a.lng) });
        started = true;
      } else {
        result.push(a);
      }
      if (segEndS > maxS || i === geometry.length - 1) {
        result.push({ lat: a.lat + t2 * (b.lat - a.lat), lng: a.lng + t2 * (b.lng - a.lng) });
        break;
      }
    }
    cumDistM += segLen;
  }
  return result;
}

/**
 * Cumulative distance (metres) from geometry[0] to the nearest point on the polyline.
 */
export function distanceAlongRoute(pt: LatLng, geometry: LatLng[]): number {
  let cumDist = 0;
  let nearest = Infinity;
  let nearestAlong = 0;

  for (let i = 1; i < geometry.length; i++) {
    const a = geometry[i - 1];
    const b = geometry[i];
    const segLen = haversine(a, b);
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    const lenSq = dx * dx + dy * dy;
    const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1,
      ((pt.lng - a.lng) * dx + (pt.lat - a.lat) * dy) / lenSq));
    const dist = haversine(pt, { lat: a.lat + t * dy, lng: a.lng + t * dx });
    if (dist < nearest) { nearest = dist; nearestAlong = cumDist + t * segLen; }
    cumDist += segLen;
  }
  return nearestAlong;
}

// ─── Polygon builders ─────────────────────────────────────────────────────────

export function buildBufferPolygon(segment: LatLng[], widthMetres: number): GeoJSON.Feature {
  const line = turf.lineString(segment.map(p => [p.lng, p.lat]));
  return turf.buffer(line, widthMetres, { units: "metres" }) as GeoJSON.Feature;
}

export function simplifyPolygon(polygon: GeoJSON.Feature, tolerance = 0.002): GeoJSON.Feature {
  return turf.simplify(polygon, { tolerance, highQuality: true }) as GeoJSON.Feature;
}

/** Convert a polygon ring to Overpass QL poly string: "lat lon lat lon ..." (lat first). */
export function polyToOverpassString(polygon: GeoJSON.Feature): string {
  const ring = (polygon.geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
  return ring.map(([lng, lat]) => `${lat.toFixed(5)} ${lng.toFixed(5)}`).join(" ");
}

export function polygonToLatLngs(polygon: GeoJSON.Feature): [number, number][] {
  const ring = (polygon.geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
  return ring.map(([lng, lat]) => [lat, lng] as [number, number]);
}
